import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Save, Check, MessageSquareWarning, Eye, Pencil, Columns2, Loader2,
  AlertTriangle, CheckCircle2, XCircle, ListTree, ChevronDown, MoreHorizontal, RefreshCw, Copy,
  X, Info, ShieldCheck, Clock, Sparkles, History,
} from 'lucide-react';
import API_BASE_URL from '../../Config/config';
import EliteContentDocument, { ELITE_CONTENT_DOC_CSS, extractOutline } from './EliteContentDocument';
import { AiRevisionDialog, RevisionHistoryDialog } from './EliteContentAiRevision';
import {
  statusLabel, statusTone, EDITABLE_STATUSES, APPROVABLE_STATUSES, REQUEST_CHANGES_STATUSES, generationMonitorPath, contentWorkspacePath,
} from './eliteContentStatus';

const EliteMarkdownEditor = lazy(() => import('./EliteMarkdownEditor'));

/**
 * EliteContentWorkspace — full-screen READ → REVIEW → EDIT → VALIDATE → SAVE
 * → APPROVE surface for one generated Elite Mastery topic.
 *
 * Route: /superadmin/elite-technologies/content/:contentId?tech=<slug>
 *   :contentId = elite_technology_topics.id (what every existing content API
 *   is keyed on: GET/PUT .../content/topics/:topicId).
 *
 * Reuses, never duplicates:
 *   GET  .../content/status                  technology + section/topic ordering (breadcrumb, prev/next)
 *   GET  .../content/topics/:id              markdown, status, score, quality_checks, review_notes
 *   PUT  .../content/topics/:id              { markdown, approve:false, expectedUpdatedAt }  → save (re-review)
 *   POST .../content/topics/:id/approve      approve
 *   POST .../content/topics/:id/request-changes { feedback }
 *   POST .../content/topics/:id/regenerate   (More menu)
 *   POST/GET .../content/topics/:id/revisions[/:rid[/apply|/reject]]   ✨ AI Edit Content (27-Aug-2026)
 *                                            — see EliteContentAiRevision.jsx. A revision is only ever a DRAFT
 *                                            until the admin applies it; applying makes it the editable draft here.
 * Markdown is the source of truth end to end; the server recomputes the
 * structural quality checks on every save.
 */

// ─── api ─────────────────────────────────────────────────────────────────────
const getUserId = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user?.userId || user?.user_id || localStorage.getItem('user_id') || null;
  } catch {
    return localStorage.getItem('user_id') || null;
  }
};
const api = async (path, opts = {}) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), opts.timeoutMs || 30000);
  try {
    const res = await fetch(`${API_BASE_URL}${path}`, { headers: { 'Content-Type': 'application/json', 'user-id': getUserId() || '' }, signal: controller.signal, ...opts });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.success === false) {
      const friendly = res.status === 401 ? 'Your session is not authorised for this page (admin sign-in required).' : res.status === 403 ? 'Admin access required.' : null;
      const err = new Error(friendly || data.error || `HTTP ${res.status}`);
      err.status = res.status; err.data = data; throw err;
    }
    return data;
  } catch (e) {
    if (e.name === 'AbortError') { const t = new Error('The request timed out. Please check your connection and retry.'); t.status = 0; throw t; }
    throw e;
  } finally { clearTimeout(timer); }
};
const topicApi = (slug, id) => `/api/admin/elite-technologies/${encodeURIComponent(slug)}/content/topics/${encodeURIComponent(id)}`;

// status (roadmap ordering) is shared across topic navigations — no reload per topic
const statusCache = new Map(); // slug → { at, data }
const STATUS_TTL_MS = 60000;
const loadStatus = async (slug, { force = false } = {}) => {
  const hit = statusCache.get(slug);
  if (!force && hit && Date.now() - hit.at < STATUS_TTL_MS) return hit.data;
  const data = await api(`/api/admin/elite-technologies/${encodeURIComponent(slug)}/content/status`);
  statusCache.set(slug, { at: Date.now(), data });
  return data;
};

const draftKey = (slug, id) => `elite-content-draft:${slug}:${id}`;
const AUTOSAVE_KEY = 'elite-content-workspace:autosave';
const MODE_KEY = 'elite-content-workspace:mode';

const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(() => (typeof window !== 'undefined' ? window.matchMedia(query).matches : false));
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = (e) => setMatches(e.matches);
    setMatches(mq.matches);
    mq.addEventListener ? mq.addEventListener('change', onChange) : mq.addListener(onChange);
    return () => { mq.removeEventListener ? mq.removeEventListener('change', onChange) : mq.removeListener(onChange); };
  }, [query]);
  return matches;
};

const useDebounced = (value, ms) => {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
};

const fmtWhen = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  return sameDay ? `Today ${d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}` : d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// ─── small UI atoms (Tailwind, admin-console language) ───────────────────────
const Pill = ({ tone, children, title }) => (
  <span title={title} className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${tone}`}>{children}</span>
);
const IconBtn = ({ label, onClick, disabled, active, children, className = '', kbd }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    aria-pressed={active}
    title={kbd ? `${label} (${kbd})` : label}
    className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-bold transition disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-500 ${active ? 'bg-violet-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'} ${className}`}
  >
    {children}
  </button>
);
const PrimaryBtn = ({ onClick, disabled, children, tone = 'violet', title, className = '' }) => {
  const bg = tone === 'green' ? 'linear-gradient(135deg,#059669,#10B981)' : 'linear-gradient(135deg,#5B21B6,#7C3AED)';
  return (
    <button type="button" onClick={onClick} disabled={disabled} title={title} className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-bold text-white shadow-sm transition disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-500 ${className}`} style={{ background: bg }}>
      {children}
    </button>
  );
};
const GhostBtn = ({ onClick, disabled, children, className = '', title, danger, ...rest }) => (
  <button type="button" onClick={onClick} disabled={disabled} title={title} {...rest} className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold border transition disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-500 ${danger ? 'border-red-200 text-red-600 hover:bg-red-50' : 'border-gray-200 text-gray-700 hover:bg-gray-50'} ${className}`}>
    {children}
  </button>
);

/** Accessible modal: focus trap-lite (autofocus first control), Esc closes, backdrop click closes. */
const Dialog = ({ open, title, children, onClose, labelledBy = 'ecw-dialog-title', wide }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.activeElement;
    const first = ref.current && ref.current.querySelector('button, textarea, input, [tabindex]:not([tabindex="-1"])');
    if (first) first.focus();
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); if (prev && prev.focus) prev.focus(); };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center p-3" role="presentation">
      <div className="absolute inset-0 bg-gray-900/40 backdrop-blur-[2px]" onClick={onClose} aria-hidden="true" />
      <div ref={ref} role="dialog" aria-modal="true" aria-labelledby={labelledBy} className={`relative w-full ${wide ? 'max-w-2xl' : 'max-w-md'} bg-white rounded-2xl shadow-2xl border border-gray-100 p-5`}>
        <div className="flex items-start justify-between gap-3 mb-3">
          <h2 id={labelledBy} className="text-base font-black text-gray-900">{title}</h2>
          <button type="button" onClick={onClose} aria-label="Close dialog" className="p-1 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-500"><X size={16} /></button>
        </div>
        {children}
      </div>
    </div>
  );
};

const Toasts = ({ items, dismiss }) => (
  <div className="fixed bottom-4 right-4 z-[130] space-y-2 w-[min(360px,calc(100vw-2rem))]" aria-live="polite" aria-atomic="false">
    {items.map((t) => (
      <div key={t.id} role="status" className={`flex items-start gap-2 p-3 rounded-xl shadow-lg border text-xs font-semibold bg-white ${t.type === 'error' ? 'border-red-200 text-red-700' : t.type === 'success' ? 'border-emerald-200 text-emerald-700' : 'border-gray-200 text-gray-700'}`}>
        {t.type === 'error' ? <XCircle size={14} className="mt-0.5 shrink-0" /> : t.type === 'success' ? <CheckCircle2 size={14} className="mt-0.5 shrink-0" /> : <Info size={14} className="mt-0.5 shrink-0" />}
        <div className="flex-1">
          <div>{t.message}</div>
          {t.action && <button type="button" onClick={() => { t.action.onClick(); dismiss(t.id); }} className="mt-1 underline font-bold">{t.action.label}</button>}
        </div>
        <button type="button" onClick={() => dismiss(t.id)} aria-label="Dismiss" className="text-gray-400 hover:text-gray-700"><X size={12} /></button>
      </div>
    ))}
  </div>
);

// ─── validation (server results only — no duplicated rules) ──────────────────
const healthItems = (content) => {
  if (!content) return [];
  const c = content.quality_checks || null;
  const issues = (content.review_notes && Array.isArray(content.review_notes.issues)) ? content.review_notes.issues : [];
  const high = issues.filter((i) => i.severity === 'high');
  const items = [];
  if (!content.content_markdown) items.push({ ok: false, critical: true, text: 'No content assembled yet' });
  if (c) {
    items.push({ ok: !!c.hasAllSections, text: c.hasAllSections ? `Structure valid (${c.headings} sections)` : `Missing sections — ${c.headings} found, expected ≥26` });
    items.push({ ok: !!c.enoughCode, text: c.enoughCode ? `Code examples present (${c.codeBlocks})` : `Not enough code examples — ${c.codeBlocks} found, expected ≥6` });
    items.push({ ok: !!c.hasKnowledgeCheck, text: c.hasKnowledgeCheck ? 'Knowledge Check present' : 'Knowledge Check missing (§28)', section: '28' });
    if (c.enoughWords === false) items.push({ ok: false, text: `Short document — ${c.words?.toLocaleString?.() || c.words} words` });
  }
  // AI review notes are advisory (they are not recomputed after an admin edit),
  // so a high-severity issue warns at approval time instead of blocking it.
  items.push({ ok: high.length === 0, text: high.length === 0 ? 'No high-severity review issues' : `${high.length} high-severity AI review issue${high.length > 1 ? 's' : ''} (advisory)` , section: high[0]?.section });
  return items;
};

// ─── the page ────────────────────────────────────────────────────────────────
const EliteContentWorkspace = () => {
  const { contentId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const slug = searchParams.get('tech') || '';
  const topicId = Number(contentId);

  const isDesktopSplit = useMediaQuery('(min-width: 1280px)');
  const isWide = useMediaQuery('(min-width: 1024px)');

  const [status, setStatus] = useState(null);
  const [content, setContent] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [savedMarkdown, setSavedMarkdown] = useState('');
  const [mode, setModeState] = useState(() => { try { return localStorage.getItem(MODE_KEY) || 'preview'; } catch { return 'preview'; } });
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved | error
  const [saveError, setSaveError] = useState(null);
  const [conflict, setConflict] = useState(null); // current row from a 409
  const [busy, setBusy] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [pendingAction, setPendingAction] = useState(null); // {label, run} awaiting unsaved-changes decision
  const [approveDialog, setApproveDialog] = useState(null); // {warnings:[]}
  const [changesDialog, setChangesDialog] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [outlineOpen, setOutlineOpen] = useState(false);
  const [healthOpen, setHealthOpen] = useState(true);
  const [restorable, setRestorable] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiInitialRevision, setAiInitialRevision] = useState(null);
  const [backgroundRevision, setBackgroundRevision] = useState(null); // a revision generating while the dialog is closed
  const [historyOpen, setHistoryOpen] = useState(false);
  const [autosave, setAutosave] = useState(() => { try { return localStorage.getItem(AUTOSAVE_KEY) === '1'; } catch { return false; } });
  const editorRef = useRef(null);
  const docRef = useRef(null);
  const toastSeq = useRef(0);

  const isDirty = draft !== savedMarkdown;
  const previewMarkdown = useDebounced(draft, 250);
  const outline = useMemo(() => extractOutline(previewMarkdown), [previewMarkdown]);

  const toast = useCallback((message, type = 'info', action) => {
    const id = ++toastSeq.current;
    setToasts((t) => [...t, { id, message, type, action }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), type === 'error' ? 8000 : 4000);
  }, []);
  const dismissToast = (id) => setToasts((t) => t.filter((x) => x.id !== id));

  const setMode = useCallback((m) => {
    const next = m === 'split' && !isDesktopSplit ? 'edit' : m;
    setModeState(next);
    try { localStorage.setItem(MODE_KEY, next); } catch { /* ignore */ }
  }, [isDesktopSplit]);
  useEffect(() => { if (mode === 'split' && !isDesktopSplit) setModeState('edit'); }, [isDesktopSplit, mode]);

  // ── load ───────────────────────────────────────────────────────────────────
  const load = useCallback(async ({ keepDraft = false } = {}) => {
    if (!slug || !Number.isFinite(topicId)) { setLoadError('Missing technology or topic in the URL.'); setLoading(false); return; }
    setLoading(true); setLoadError(null); setConflict(null); setSaveError(null);
    try {
      const [st, tc] = await Promise.all([loadStatus(slug), api(topicApi(slug, topicId))]);
      setStatus(st);
      const row = tc.content;
      setContent(row);
      const md = row.content_markdown || '';
      setSavedMarkdown(md);
      if (!keepDraft) {
        setDraft(md);
        try {
          const local = localStorage.getItem(draftKey(slug, topicId));
          setRestorable(local && local !== md ? local : null);
        } catch { setRestorable(null); }
      }
      setSaveState('idle');
    } catch (err) {
      setLoadError(err.status === 404 ? 'This topic has no generated content (or does not exist).' : err.message || 'Failed to load content');
    } finally { setLoading(false); }
  }, [slug, topicId]);

  useEffect(() => { load(); }, [load]);

  // keep an unsaved draft locally so a crash / refresh never loses it
  useEffect(() => {
    try {
      if (isDirty) localStorage.setItem(draftKey(slug, topicId), draft);
      else localStorage.removeItem(draftKey(slug, topicId));
    } catch { /* storage unavailable */ }
  }, [draft, isDirty, slug, topicId]);

  // browser-level protection
  useEffect(() => {
    if (!isDirty) return undefined;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // ── navigation context ─────────────────────────────────────────────────────
  const orderedTopics = useMemo(() => {
    if (!status) return [];
    const out = [];
    for (const s of status.sections || []) for (const t of s.topics || []) out.push({ ...t, sectionTitle: s.title });
    return out;
  }, [status]);
  const position = orderedTopics.findIndex((t) => t.id === topicId);
  const hasContent = (t) => t.content && !['PENDING', 'IN_PROGRESS', 'NONE'].includes(t.content.status);
  const prevTopic = position > 0 ? [...orderedTopics.slice(0, position)].reverse().find(hasContent) : null;
  const nextTopic = position >= 0 ? orderedTopics.slice(position + 1).find(hasContent) : null;
  const tech = status?.technology;
  const qualityMin = status?.qualityMinScore || 70;

  // ── save ───────────────────────────────────────────────────────────────────
  const save = useCallback(async ({ silent = false, overwrite = false } = {}) => {
    if (!content || saveState === 'saving') return false;
    if (!isDirty && !overwrite) { if (!silent) toast('Nothing to save — the document is up to date.'); return true; }
    if (draft.trim().length < 200) { toast('Content is too short to save (at least 200 characters).', 'error'); return false; }
    setSaveState('saving'); setSaveError(null);
    try {
      const data = await api(topicApi(slug, topicId), {
        method: 'PUT',
        body: JSON.stringify({ markdown: draft, approve: false, expectedUpdatedAt: overwrite ? null : content.updated_at }),
      });
      setContent(data.content);
      setSavedMarkdown(draft);
      setSaveState('saved');
      setConflict(null);
      statusCache.delete(slug); // counts changed
      if (!silent) toast('Saved. The topic is back in Needs Review until approved.', 'success');
      setTimeout(() => setSaveState((s) => (s === 'saved' ? 'idle' : s)), 2500);
      return true;
    } catch (err) {
      setSaveState('error');
      if (err.status === 409 && err.data && err.data.current) { setConflict(err.data.current); return false; }
      setSaveError(err.message || 'Unable to save changes');
      toast(`Unable to save changes — ${err.message || 'please retry'}. Your changes are still available locally.`, 'error', { label: 'Retry save', onClick: () => save() });
      return false;
    }
  }, [content, saveState, isDirty, draft, slug, topicId, toast]);

  // autosave (optional, debounced; never every keystroke)
  useEffect(() => {
    if (!autosave || !isDirty || saveState === 'saving' || conflict) return undefined;
    const t = setTimeout(() => { save({ silent: true }); }, 3000);
    return () => clearTimeout(t);
  }, [autosave, draft, isDirty, saveState, conflict, save]);

  // keyboard shortcuts
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') { e.preventDefault(); if (isDirty) save(); }
      if (e.key === 'Escape') { setMenuOpen(false); setOutlineOpen(false); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isDirty, save]);

  // ── unsaved-change guard ───────────────────────────────────────────────────
  const guard = useCallback((label, run) => {
    if (isDirty) setPendingAction({ label, run });
    else run();
  }, [isDirty]);
  const resolvePending = async (choice) => {
    const p = pendingAction; setPendingAction(null);
    if (!p) return;
    if (choice === 'save') { const ok = await save({ silent: true }); if (ok) p.run(); return; }
    if (choice === 'discard') { setDraft(savedMarkdown); try { localStorage.removeItem(draftKey(slug, topicId)); } catch { /* ignore */ } p.run(); }
  };

  const goBack = () => guard('leave this page', () => navigate(generationMonitorPath(slug)));
  const goTo = (t) => guard('open another topic', () => navigate(contentWorkspacePath(slug, t.id)));

  // ── review actions ─────────────────────────────────────────────────────────
  const approve = async () => {
    if (!content) return;
    if (isDirty) { setPendingAction({ label: 'approve', run: () => setTimeout(() => startApprove(), 0) }); return; }
    startApprove();
  };
  const startApprove = () => {
    const items = healthItems(content);
    const critical = items.filter((i) => i.critical && !i.ok);
    if (critical.length) { toast(`Cannot approve: ${critical.map((i) => i.text).join('; ')}`, 'error'); return; }
    const warnings = items.filter((i) => !i.ok);
    if (content.quality_score != null && content.quality_score < qualityMin) warnings.push({ text: `Quality score ${content.quality_score} is below the minimum of ${qualityMin}` });
    setApproveDialog({ warnings });
  };
  const confirmApprove = async () => {
    setApproveDialog(null); setBusy(true);
    try {
      const data = await api(`${topicApi(slug, topicId)}/approve`, { method: 'POST' });
      setContent(data.content); statusCache.delete(slug);
      toast('Topic approved.', 'success');
    } catch (err) { toast(err.message || 'Approve failed', 'error'); }
    finally { setBusy(false); }
  };
  const submitChanges = async () => {
    if (feedback.trim().length < 3) { toast('Please describe what needs to change.', 'error'); return; }
    setBusy(true);
    try {
      const data = await api(`${topicApi(slug, topicId)}/request-changes`, { method: 'POST', body: JSON.stringify({ feedback }) });
      setContent(data.content); statusCache.delete(slug); setChangesDialog(false); setFeedback('');
      toast('Changes requested. The topic is marked Needs Review.', 'success');
    } catch (err) { toast(err.message || 'Could not request changes', 'error'); }
    finally { setBusy(false); }
  };
  const regenerate = () => {
    setMenuOpen(false);
    guard('regenerate this topic', async () => {
      if (!window.confirm('Regenerate this topic from scratch? The current content (and any feedback) will be replaced when generation finishes.')) return;
      setBusy(true);
      try { await api(`${topicApi(slug, topicId)}/regenerate`, { method: 'POST' }); statusCache.delete(slug); toast('Regeneration started — track it on the Generation Monitor.', 'success'); }
      catch (err) { toast(err.message || 'Regeneration failed', 'error'); }
      finally { setBusy(false); }
    });
  };

  // ── AI Edit Content ────────────────────────────────────────────────────────
  const topicPath = topicApi(slug, topicId);
  const openAiEdit = (revision = null) => { setMenuOpen(false); setHistoryOpen(false); setAiInitialRevision(revision); setAiOpen(true); };
  const closeAiEdit = useCallback(() => { setAiOpen(false); setAiInitialRevision(null); }, []);
  /** The revision became the live content: it is now the editable draft — no reload. */
  const onRevisionApplied = (row, revision, version) => {
    const md = row.content_markdown || '';
    setContent(row); setSavedMarkdown(md); setDraft(md); setConflict(null); setSaveError(null); setSaveState('saved');
    try { localStorage.removeItem(draftKey(slug, topicId)); } catch { /* ignore */ }
    statusCache.delete(slug);
    setBackgroundRevision(null); closeAiEdit();
    toast(`Changes applied${version ? ` — version ${version}` : ''}. The topic is in Needs Review until approved.`, 'success');
    setTimeout(() => setSaveState((s) => (s === 'saved' ? 'idle' : s)), 2500);
  };
  // keep an eye on a revision that was sent to the background
  useEffect(() => {
    if (!backgroundRevision || backgroundRevision.status !== 'GENERATING' || aiOpen) return undefined;
    let cancelled = false;
    const t = setInterval(async () => {
      try {
        const data = await api(`${topicPath}/revisions/${backgroundRevision.id}`);
        if (cancelled) return;
        setBackgroundRevision(data.revision);
        if (data.revision.status === 'DRAFT') toast('Revision ready — review the changes when you are ready.', 'success', { label: 'Review changes', onClick: () => openAiEdit(data.revision) });
        else if (data.revision.status !== 'GENERATING') toast(`Revision failed: ${data.revision.error || 'unknown error'}. Your content is unchanged.`, 'error');
      } catch { /* transient; keep polling */ }
    }, 3000);
    return () => { cancelled = true; clearInterval(t); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backgroundRevision?.id, backgroundRevision?.status, aiOpen, topicPath]);

  // ── outline / issue navigation ─────────────────────────────────────────────
  const scrollToHeading = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setOutlineOpen(false);
  };
  const revealSection = (sectionNo) => {
    const lines = draft.split('\n');
    const idx = lines.findIndex((l) => new RegExp(`^##\\s+${sectionNo}\\.`).test(l));
    if (mode === 'preview') {
      const h = outline.find((o) => new RegExp(`^${sectionNo}\\.`).test(o.text));
      if (h) scrollToHeading(h.id); else toast(`Section ${sectionNo} is not in the document.`, 'info');
      return;
    }
    if (idx >= 0 && editorRef.current) editorRef.current.revealLine(idx + 1);
    else toast(`Section ${sectionNo} is not in the document — add "## ${sectionNo}. …".`, 'info');
  };

  // ── render ─────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F6F7FB] flex items-center justify-center" aria-busy="true">
        <style>{ELITE_CONTENT_DOC_CSS}</style>
        <div className="flex flex-col items-center gap-3 text-sm text-gray-500"><Loader2 size={26} className="animate-spin text-violet-500" />Loading content…</div>
      </div>
    );
  }
  if (loadError || !content) {
    return (
      <div className="min-h-screen bg-[#F6F7FB] flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 max-w-md w-full text-center">
          <AlertTriangle size={26} className="mx-auto mb-2 text-amber-500" />
          <h1 className="font-black text-gray-900 mb-1">Unable to open this topic</h1>
          <p className="text-sm text-gray-500 mb-4">{loadError || 'Content not found.'}</p>
          <div className="flex gap-2 justify-center">
            <GhostBtn onClick={() => load()}><RefreshCw size={13} /> Retry</GhostBtn>
            <PrimaryBtn onClick={() => navigate(slug ? generationMonitorPath(slug) : '/superadmin/elite-technologies')}><ArrowLeft size={13} /> Back to Roadmap</PrimaryBtn>
          </div>
        </div>
      </div>
    );
  }

  const editable = EDITABLE_STATUSES.includes(content.status);
  const canApprove = APPROVABLE_STATUSES.includes(content.status) && !!content.content_markdown;
  const canRequestChanges = REQUEST_CHANGES_STATUSES.includes(content.status);
  const health = healthItems(content);
  const aiIssues = (content.review_notes && Array.isArray(content.review_notes.issues)) ? content.review_notes.issues : [];
  const adminFeedback = (content.review_notes && Array.isArray(content.review_notes.adminFeedback)) ? content.review_notes.adminFeedback : [];
  const lastEdit = content.review_notes && content.review_notes.lastEdit;
  const scoreTone = content.quality_score == null ? 'bg-gray-100 text-gray-500' : content.quality_score >= qualityMin ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700';
  const showEditor = mode === 'edit' || mode === 'split';
  const showPreview = mode === 'preview' || mode === 'split';
  const saveLabel = saveState === 'saving' ? 'Saving…' : saveState === 'saved' ? 'Saved' : isDirty ? 'Save' : 'Saved';

  const OutlineList = ({ compact }) => (
    <nav aria-label="On this page" className={compact ? '' : 'text-[12px]'}>
      {outline.length === 0 ? <p className="text-xs text-gray-400">No headings yet.</p> : (
        <ol className="space-y-0.5">
          {outline.filter((h) => h.level >= 2).map((h) => (
            <li key={h.id} className={h.level === 3 ? 'pl-3' : ''}>
              <button type="button" onClick={() => scrollToHeading(h.id)} className={`w-full text-left px-2 py-1 rounded-md hover:bg-violet-50 hover:text-violet-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-500 ${h.level === 3 ? 'text-gray-500' : 'font-semibold text-gray-700'} truncate`} title={h.text}>{h.text}</button>
            </li>
          ))}
        </ol>
      )}
    </nav>
  );

  return (
    <div className="min-h-screen bg-[#F6F7FB] flex flex-col" onClick={() => menuOpen && setMenuOpen(false)}>
      <style>{ELITE_CONTENT_DOC_CSS}</style>

      {/* ── sticky header ── */}
      <header className="sticky top-0 z-[100] bg-white/95 backdrop-blur border-b border-gray-200">
        <div className="max-w-[1600px] mx-auto px-3 sm:px-5 h-14 flex items-center gap-2 sm:gap-3">
          <button type="button" onClick={goBack} className="inline-flex items-center gap-1 text-xs font-bold text-gray-500 hover:text-gray-900 shrink-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-500 rounded-md px-1 py-1" title="Back to Roadmap">
            <ArrowLeft size={16} /><span className="hidden sm:inline">Back to Roadmap</span>
          </button>
          <nav aria-label="Breadcrumb" className="min-w-0 flex-1 hidden md:flex items-center text-xs text-gray-500 gap-1 truncate">
            <span className="font-semibold text-gray-700 truncate">{tech?.title || slug}</span>
            <span aria-hidden="true">→</span>
            <span className="truncate">{content.section_title || '—'}</span>
            <span aria-hidden="true">→</span>
            <span className="font-bold text-gray-900 truncate">{content.title}</span>
          </nav>
          <div className="md:hidden min-w-0 flex-1 text-xs font-bold text-gray-900 truncate">{content.title}</div>

          {/* review status + unsaved state stay visible while scrolled */}
          <div className="hidden lg:flex items-center gap-1.5 shrink-0" aria-live="polite">
            <Pill tone={statusTone(content.status)}>{statusLabel(content.status)}</Pill>
            {isDirty && <Pill tone="bg-violet-50 text-violet-700">Unsaved changes</Pill>}
          </div>

          <div className="flex items-center gap-1 sm:gap-1.5 shrink-0" role="group" aria-label="View mode">
            <IconBtn label="Preview" active={mode === 'preview'} onClick={() => setMode('preview')}><Eye size={14} /><span className="hidden sm:inline">Preview</span></IconBtn>
            <IconBtn label="Edit" active={mode === 'edit'} onClick={() => setMode('edit')} disabled={!editable}><Pencil size={14} /><span className="hidden sm:inline">Edit</span></IconBtn>
            {isDesktopSplit && <IconBtn label="Split view" active={mode === 'split'} onClick={() => setMode('split')} disabled={!editable}><Columns2 size={14} /><span className="hidden xl:inline">Split</span></IconBtn>}
          </div>
          <div className="hidden lg:block w-px h-6 bg-gray-200" />
          <div className="flex items-center gap-1.5 shrink-0">
            <PrimaryBtn onClick={() => save()} disabled={!isDirty || saveState === 'saving' || !editable} title="Save (Ctrl/Cmd + S)">
              {saveState === 'saving' ? <Loader2 size={14} className="animate-spin" /> : saveState === 'saved' || !isDirty ? <Check size={14} /> : <Save size={14} />}
              <span>{saveLabel}</span>
            </PrimaryBtn>
            {editable && (
              <PrimaryBtn onClick={() => openAiEdit(backgroundRevision && ['GENERATING', 'DRAFT'].includes(backgroundRevision.status) ? backgroundRevision : null)} disabled={busy} title="Describe what should change and let AI revise only the affected sections">
                <Sparkles size={14} /><span className="hidden sm:inline">AI Edit</span>
              </PrimaryBtn>
            )}
            {canApprove && <PrimaryBtn tone="green" onClick={approve} disabled={busy} title={isDirty ? 'Save first, then approve' : 'Approve this topic'}><ShieldCheck size={14} /><span className="hidden sm:inline">Approve</span></PrimaryBtn>}
            {canRequestChanges && <GhostBtn onClick={() => setChangesDialog(true)} disabled={busy} title="Request changes" className="hidden sm:inline-flex"><MessageSquareWarning size={14} /><span className="hidden lg:inline">Request Changes</span></GhostBtn>}
            <div className="relative">
              <IconBtn label="More actions" onClick={(e) => { e.stopPropagation(); setMenuOpen((o) => !o); }} active={menuOpen}><MoreHorizontal size={16} /></IconBtn>
              {menuOpen && (
                <div role="menu" className="absolute right-0 mt-1 w-56 bg-white rounded-xl shadow-xl border border-gray-100 p-1 text-xs" onClick={(e) => e.stopPropagation()}>
                  {canRequestChanges && <button role="menuitem" type="button" onClick={() => { setMenuOpen(false); setChangesDialog(true); }} className="sm:hidden w-full text-left px-3 py-2 rounded-lg hover:bg-gray-50 flex items-center gap-2"><MessageSquareWarning size={13} /> Request changes</button>}
                  <button role="menuitem" type="button" onClick={() => { setMenuOpen(false); guard('reload from server', () => load()); }} className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-50 flex items-center gap-2"><RefreshCw size={13} /> Reload from server</button>
                  <button role="menuitem" type="button" onClick={() => { setMenuOpen(false); try { navigator.clipboard.writeText(draft); toast('Markdown copied.', 'success'); } catch { toast('Clipboard unavailable', 'error'); } }} className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-50 flex items-center gap-2"><Copy size={13} /> Copy markdown</button>
                  <label className="w-full px-3 py-2 rounded-lg hover:bg-gray-50 flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={autosave} onChange={(e) => { setAutosave(e.target.checked); try { localStorage.setItem(AUTOSAVE_KEY, e.target.checked ? '1' : '0'); } catch { /* ignore */ } }} /> Autosave while editing
                  </label>
                  <button role="menuitem" type="button" onClick={() => { setMenuOpen(false); setHistoryOpen(true); }} className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-50 flex items-center gap-2"><History size={13} /> Revision history</button>
                  <div className="my-1 border-t border-gray-100" />
                  <button role="menuitem" type="button" onClick={regenerate} disabled={busy} className="w-full text-left px-3 py-2 rounded-lg hover:bg-red-50 text-red-600 flex items-center gap-2"><RefreshCw size={13} /> Regenerate topic</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* ── info bar ── */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-[1600px] mx-auto px-3 sm:px-5 py-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-[12px] text-gray-500">
          <h1 className="text-sm sm:text-base font-black text-gray-900 w-full sm:w-auto">{content.title}</h1>
          <span className="hidden sm:inline">Section: <b className="text-gray-700">{content.section_title || '—'}</b></span>
          <span className="hidden sm:inline">Technology: <b className="text-gray-700">{tech?.title || slug}</b></span>
          <Pill tone={statusTone(content.status)}>{statusLabel(content.status)}</Pill>
          <Pill tone={scoreTone} title={`Quality score (minimum ${qualityMin})`}>Score: {content.quality_score ?? '—'}</Pill>
          {content.word_count != null && <span>{content.word_count.toLocaleString()} words</span>}
          <span className="inline-flex items-center gap-1"><Clock size={12} /> Updated {fmtWhen(content.updated_at)}{lastEdit?.by ? ` by ${lastEdit.by}` : content.approved_by ? ` · approved by ${content.approved_by}` : ''}</span>
          {isDirty && <Pill tone="bg-violet-50 text-violet-700">Unsaved changes</Pill>}
          {position >= 0 && <span className="ml-auto">Topic {position + 1} of {orderedTopics.length}</span>}
        </div>
      </div>

      {/* ── banners ── */}
      {restorable && (
        <div className="bg-violet-50 border-b border-violet-100 text-violet-900 text-xs px-4 py-2 flex flex-wrap items-center gap-2 justify-center">
          <Info size={13} /> An unsaved draft from a previous session was found for this topic.
          <GhostBtn onClick={() => { setDraft(restorable); setRestorable(null); setMode('edit'); }}>Restore draft</GhostBtn>
          <GhostBtn onClick={() => { setRestorable(null); try { localStorage.removeItem(draftKey(slug, topicId)); } catch { /* ignore */ } }}>Discard</GhostBtn>
        </div>
      )}
      {backgroundRevision && !aiOpen && (backgroundRevision.status === 'GENERATING' || backgroundRevision.status === 'DRAFT') && (
        <div className="bg-violet-50 border-b border-violet-100 text-violet-900 text-xs px-4 py-2 flex flex-wrap items-center gap-2 justify-center" role="status" data-testid="ai-background-banner">
          {backgroundRevision.status === 'GENERATING' ? <><Loader2 size={13} className="animate-spin" /> Generating revision in the background — your content is unchanged until you apply it.</> : <><CheckCircle2 size={13} /> Revision ready.</>}
          <GhostBtn onClick={() => openAiEdit(backgroundRevision)}>{backgroundRevision.status === 'GENERATING' ? 'View progress' : 'Review changes'}</GhostBtn>
          {backgroundRevision.status === 'DRAFT' && <GhostBtn onClick={() => setBackgroundRevision(null)}>Later</GhostBtn>}
        </div>
      )}
      {saveError && (
        <div className="bg-red-50 border-b border-red-100 text-red-800 text-xs px-4 py-2 flex flex-wrap items-center gap-2 justify-center" role="alert">
          <XCircle size={13} /> Unable to save changes — {saveError}. Your changes are still available locally.
          <GhostBtn onClick={() => save()}><RefreshCw size={12} /> Retry save</GhostBtn>
        </div>
      )}
      {!editable && (
        <div className="bg-gray-50 border-b border-gray-100 text-gray-600 text-xs px-4 py-2 text-center">This topic is {statusLabel(content.status).toLowerCase()} — editing is available once content has been generated.</div>
      )}

      {/* ── workspace ── */}
      <div className="flex-1">
        <div className={`max-w-[1600px] mx-auto px-3 sm:px-5 py-5 ${isWide && mode === 'preview' ? 'grid grid-cols-[minmax(0,1fr)_260px] gap-6' : ''}`}>
          <div className="min-w-0">
            {/* Content health + review notes (compact, collapsible) */}
            <section className="bg-white rounded-2xl border border-gray-100 shadow-sm mb-4" aria-labelledby="ecw-health">
              <button type="button" onClick={() => setHealthOpen((o) => !o)} className="w-full flex items-center justify-between px-4 py-2.5 text-left" aria-expanded={healthOpen}>
                <span id="ecw-health" className="text-[11px] font-black text-gray-400 uppercase tracking-wide inline-flex items-center gap-2">Content Health {health.some((i) => !i.ok) ? <AlertTriangle size={12} className="text-amber-500" /> : <CheckCircle2 size={12} className="text-emerald-500" />}</span>
                <span className="text-[11px] text-gray-500 inline-flex items-center gap-2">Score {content.quality_score ?? '—'}{isDirty && ' · checks reflect the last saved version'}<ChevronDown size={14} className={`transition ${healthOpen ? 'rotate-180' : ''}`} /></span>
              </button>
              {healthOpen && (
                <div className="px-4 pb-4 grid gap-3 md:grid-cols-2">
                  <ul className="space-y-1 text-[12px]">
                    {health.map((i, idx) => (
                      <li key={idx} className={`flex items-start gap-2 ${i.ok ? 'text-gray-600' : i.critical ? 'text-red-700' : 'text-amber-700'}`}>
                        {i.ok ? <CheckCircle2 size={14} className="text-emerald-500 mt-0.5 shrink-0" aria-label="passed" /> : <AlertTriangle size={14} className={`${i.critical ? 'text-red-500' : 'text-amber-500'} mt-0.5 shrink-0`} aria-label="needs attention" />}
                        {i.section && !i.ok ? <button type="button" onClick={() => revealSection(i.section)} className="text-left underline decoration-dotted hover:text-violet-700">{i.text}</button> : <span>{i.text}</span>}
                      </li>
                    ))}
                    {health.length === 0 && <li className="text-gray-400">No validation results yet.</li>}
                  </ul>
                  <div className="space-y-2 text-[12px]">
                    {aiIssues.length > 0 && (
                      <div className="p-3 rounded-lg bg-amber-50 border border-amber-100">
                        <p className="font-bold text-amber-800 mb-1">AI review{content.review_notes?.summary ? ` — ${content.review_notes.summary}` : ''}</p>
                        <ul className="space-y-1 text-amber-900">
                          {aiIssues.map((i, idx) => (
                            <li key={idx}>
                              <b className="uppercase text-[10px]">{i.severity}</b> ·{' '}
                              <button type="button" onClick={() => revealSection(i.section)} className="underline decoration-dotted hover:text-violet-700">§{i.section}</button>: {i.issue}
                              {i.suggested_fix ? <span className="text-amber-700"> — fix: {i.suggested_fix}</span> : null}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {adminFeedback.length > 0 && (
                      <div className="p-3 rounded-lg bg-violet-50 border border-violet-100">
                        <p className="font-bold text-violet-800 mb-1">Reviewer feedback</p>
                        <ul className="space-y-1 text-violet-900">
                          {adminFeedback.slice().reverse().map((f, idx) => <li key={idx}><span className="text-violet-600">{f.by || 'admin'} · {fmtWhen(f.at)}</span> — {f.message}</li>)}
                        </ul>
                      </div>
                    )}
                    {aiIssues.length === 0 && adminFeedback.length === 0 && <p className="text-gray-400">No review notes.</p>}
                  </div>
                </div>
              )}
            </section>

            {/* Outline dropdown for narrow screens */}
            {(!isWide || mode !== 'preview') && showPreview && (
              <div className="mb-3 relative">
                <GhostBtn onClick={() => setOutlineOpen((o) => !o)} title="On this page"><ListTree size={13} /> On this page ({outline.filter((h) => h.level >= 2).length})</GhostBtn>
                {outlineOpen && <div className="absolute z-[90] mt-1 w-[min(360px,90vw)] max-h-[60vh] overflow-y-auto bg-white rounded-xl shadow-xl border border-gray-100 p-2 text-[12px]"><OutlineList compact /></div>}
              </div>
            )}

            {/* Document / editor */}
            <div className={mode === 'split' ? 'grid grid-cols-2 gap-4 items-start' : ''}>
              {showEditor && (
                <section aria-label="Markdown editor" className={mode === 'split' ? 'sticky top-[64px] max-h-[calc(100vh-80px)] overflow-hidden flex flex-col' : ''}>
                  <Suspense fallback={<textarea aria-label="Markdown editor (loading)" value={draft} onChange={(e) => setDraft(e.target.value)} className="w-full min-h-[60vh] font-mono text-[13px] p-4 rounded-xl border border-gray-200 bg-white" spellCheck={false} />}>
                    <div className={mode === 'split' ? 'overflow-y-auto' : ''}>
                      <EliteMarkdownEditor ref={editorRef} value={draft} onChange={(v) => setDraft(v)} readOnly={!editable} minHeight={mode === 'split' ? 'calc(100vh - 100px)' : '70vh'} />
                    </div>
                  </Suspense>
                </section>
              )}
              {showPreview && (
                <section aria-label="Rendered preview" ref={docRef} className={`bg-white rounded-2xl border border-gray-100 shadow-sm ${mode === 'split' ? 'px-6 py-8' : 'px-5 sm:px-10 lg:px-14 py-8 sm:py-12'}`}>
                  <div className={mode === 'split' ? '' : 'max-w-[980px] mx-auto'}>
                    {previewMarkdown ? <EliteContentDocument markdown={previewMarkdown} /> : <p className="text-sm text-gray-400">No content assembled yet.</p>}
                  </div>
                </section>
              )}
            </div>

            {/* bottom navigation */}
            <nav aria-label="Topic navigation" className="mt-6 flex items-center justify-between gap-3">
              <GhostBtn onClick={() => prevTopic && goTo(prevTopic)} disabled={!prevTopic} aria-label={prevTopic ? `Previous topic: ${prevTopic.title}` : 'No previous topic with content'} title={prevTopic ? prevTopic.title : 'No previous topic with content'}><ArrowLeft size={14} /><span className="truncate max-w-[40vw]">{prevTopic ? prevTopic.title : 'Previous topic'}</span></GhostBtn>
              <span className="text-[11px] text-gray-500 hidden sm:inline">{position >= 0 ? `Topic ${position + 1} of ${orderedTopics.length}` : ''}</span>
              <GhostBtn onClick={() => nextTopic && goTo(nextTopic)} disabled={!nextTopic} aria-label={nextTopic ? `Next topic: ${nextTopic.title}` : 'No next topic with content'} title={nextTopic ? nextTopic.title : 'No next topic with content'}><span className="truncate max-w-[40vw]">{nextTopic ? nextTopic.title : 'Next topic'}</span><ArrowRight size={14} /></GhostBtn>
            </nav>
          </div>

          {/* outline sidebar (desktop preview) */}
          {isWide && mode === 'preview' && (
            <aside className="hidden lg:block">
              <div className="sticky top-[76px] bg-white rounded-2xl border border-gray-100 shadow-sm p-3 max-h-[calc(100vh-100px)] overflow-y-auto">
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-wide mb-2 px-2">On this page</p>
                <OutlineList />
              </div>
            </aside>
          )}
        </div>
      </div>

      {/* ── dialogs ── */}
      <Dialog open={!!pendingAction} title="Unsaved changes" onClose={() => setPendingAction(null)}>
        <p className="text-sm text-gray-600 mb-4">You have unsaved changes. What would you like to do before you {pendingAction?.label}?</p>
        <div className="flex flex-wrap gap-2 justify-end">
          <GhostBtn onClick={() => setPendingAction(null)}>Cancel</GhostBtn>
          <GhostBtn danger onClick={() => resolvePending('discard')}>Discard changes</GhostBtn>
          <PrimaryBtn onClick={() => resolvePending('save')} disabled={saveState === 'saving'}>{saveState === 'saving' ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save & continue</PrimaryBtn>
        </div>
      </Dialog>

      <Dialog open={!!conflict} title="Someone else changed this topic" onClose={() => setConflict(null)} wide>
        <p className="text-sm text-gray-600 mb-2">This topic was modified{conflict?.review_notes?.lastEdit?.by ? ` by ${conflict.review_notes.lastEdit.by}` : ''} at {fmtWhen(conflict?.updated_at)} after you opened it. Your save was not applied so their work is not lost.</p>
        <p className="text-xs text-gray-500 mb-4">Reload their version (your unsaved text stays in local draft storage and can be restored), or overwrite it with yours.</p>
        <div className="flex flex-wrap gap-2 justify-end">
          <GhostBtn onClick={() => setConflict(null)}>Cancel</GhostBtn>
          <GhostBtn onClick={() => { try { localStorage.setItem(draftKey(slug, topicId), draft); } catch { /* ignore */ } load(); }}><RefreshCw size={13} /> Reload their version</GhostBtn>
          <PrimaryBtn onClick={() => { setContent(conflict); setConflict(null); save({ overwrite: true }); }}><Save size={13} /> Overwrite with mine</PrimaryBtn>
        </div>
      </Dialog>

      <Dialog open={!!approveDialog} title="Approve this topic?" onClose={() => setApproveDialog(null)}>
        {approveDialog?.warnings?.length ? (
          <>
            <p className="text-sm text-gray-600 mb-2">Validation found items that should be fixed before approval:</p>
            <ul className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg p-3 mb-4 space-y-1">
              {approveDialog.warnings.map((w, i) => <li key={i} className="flex gap-2"><AlertTriangle size={12} className="mt-0.5 shrink-0" />{w.text}</li>)}
            </ul>
            <p className="text-xs text-gray-500 mb-4">You can still approve if the content is acceptable as-is.</p>
          </>
        ) : <p className="text-sm text-gray-600 mb-4">All checks passed. The topic will be marked <b>Approved</b> and included in the next publish.</p>}
        <div className="flex gap-2 justify-end">
          <GhostBtn onClick={() => setApproveDialog(null)}>Cancel</GhostBtn>
          <PrimaryBtn tone="green" onClick={confirmApprove}><ShieldCheck size={13} /> {approveDialog?.warnings?.length ? 'Approve anyway' : 'Approve'}</PrimaryBtn>
        </div>
      </Dialog>

      <Dialog open={changesDialog} title="Request changes" onClose={() => setChangesDialog(false)}>
        <label htmlFor="ecw-feedback" className="block text-xs font-bold text-gray-600 mb-1">What needs to be improved?</label>
        <textarea id="ecw-feedback" value={feedback} onChange={(e) => setFeedback(e.target.value)} rows={5} className="w-full text-sm p-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-violet-300" placeholder="Be specific — the section, what is wrong, and what good looks like." />
        <div className="flex gap-2 justify-end mt-3">
          <GhostBtn onClick={() => setChangesDialog(false)}>Cancel</GhostBtn>
          <PrimaryBtn onClick={submitChanges} disabled={busy || feedback.trim().length < 3}>{busy ? <Loader2 size={13} className="animate-spin" /> : <MessageSquareWarning size={13} />} Request changes</PrimaryBtn>
        </div>
      </Dialog>

      <AiRevisionDialog
        open={aiOpen}
        onClose={closeAiEdit}
        api={api}
        topicPath={topicPath}
        content={content}
        draft={draft}
        savedMarkdown={savedMarkdown}
        initialRevision={aiInitialRevision}
        qualityMin={qualityMin}
        onApplied={onRevisionApplied}
        onReloadLatest={() => { try { if (isDirty) localStorage.setItem(draftKey(slug, topicId), draft); } catch { /* ignore */ } setBackgroundRevision(null); load(); }}
        onBackground={(rev) => setBackgroundRevision(rev)}
        toast={toast}
      />
      <RevisionHistoryDialog open={historyOpen} onClose={() => setHistoryOpen(false)} api={api} topicPath={topicPath} onOpenRevision={(r) => openAiEdit(r)} />

      <Toasts items={toasts} dismiss={dismissToast} />
    </div>
  );
};

export default EliteContentWorkspace;
